import { telephonyApi } from 'app/services/api';

import { CallStatus, TelephonySignalREventType } from 'constants/telephony';
import { jsonParse } from 'utils/json';

import { TELEPHONY_ACTION_TYPE } from 'store/system/actions/telephony.actions';

class SignalRTelephonyController {
  #connection;
  #dispatch;
  #getState;
  #logger;

  constructor(connection, logger) {
    this.#connection = connection;
    this.#logger = logger;
  }

  get connection() {
    if (!this.#connection) {
      this.#logger.error('Trying to use telephony SignalR connection without active connection');
    }

    return this.#connection;
  }

  init(dispatch, getState) {
    if (!dispatch) {
      this.#logger.error('Dispatch is required for initializing signalR telephony controller');
    }

    this.#dispatch = dispatch;
    this.#getState = getState;
  }

  startListeners() {
    this.connection.on(
      TelephonySignalREventType.UPDATE_CONTACT_STATUS,
      this.#onUpdateContactStatus.bind(this),
    );

    this.connection.on(TelephonySignalREventType.INCOMING_LEAD, this.#onIncomingLead.bind(this));

    this.connection.on(
      TelephonySignalREventType.CANCEL_NOTIFICATION,
      this.#onCancelNotification.bind(this),
    );

    this.connection.on(
      TelephonySignalREventType.ON_TEXT_MESSAGE_RECEIVED,
      this.#onTextMessageReceived.bind(this),
    );

    this.connection.on(
      TelephonySignalREventType.ON_WEB_CHAT_MESSAGE_RECEIVED,
      this.#onWebChatMessageReceived.bind(this),
    );

    this.connection.on(TelephonySignalREventType.ON_CALL_FAILED, this.#onCallFailed.bind(this));

    this.connection.on(
      TelephonySignalREventType.CONFERENCE_PARTICIPANT_JOINING,
      this.#onConferenceParticipantJoining.bind(this),
    );

    this.connection.on(
      TelephonySignalREventType.CONFERENCE_PARTICIPANT_JOINED,
      this.#onConferenceParticipantJoined.bind(this),
    );
  }

  stopListeners() {
    this.connection.off(TelephonySignalREventType.UPDATE_CONTACT_STATUS);
    this.connection.off(TelephonySignalREventType.INCOMING_LEAD);
    this.connection.off(TelephonySignalREventType.CANCEL_NOTIFICATION);
    this.connection.off(TelephonySignalREventType.ON_TEXT_MESSAGE_RECEIVED);
    this.connection.off(TelephonySignalREventType.ON_WEB_CHAT_MESSAGE_RECEIVED);
    this.connection.off(TelephonySignalREventType.ON_CALL_FAILED);
    this.connection.off(TelephonySignalREventType.CONFERENCE_PARTICIPANT_JOINING);
    this.connection.off(TelephonySignalREventType.CONFERENCE_PARTICIPANT_JOINED);
  }

  #onUpdateContactStatus(contactJson) {
    try {
      const contact = jsonParse(contactJson);

      this.#dispatch({
        type: TELEPHONY_ACTION_TYPE.UPDATE_CONTACT_STATUS,
        payload: contact,
      });
    } catch (error) {
      this.#logger.error('Failed to parse UpdateContactStatus payload', error);
    }
  }

  #onIncomingLead(messageJson, notificationId, clientStateJson) {
    try {
      const leadData = jsonParse(messageJson);
      const clientState = clientStateJson ? jsonParse(clientStateJson) : null;
      const state = this.#getState?.();
      const callStatus = state?.system?.telephony?.call?.status;

      if (callStatus === CallStatus.ACTIVE || callStatus === CallStatus.OUTGOING) {
        this.#dispatch({
          type: TELEPHONY_ACTION_TYPE.AUTO_REJECT_LEAD,
          payload: { notificationId, reason: 'OnCall' },
        });

        telephonyApi
          .rejectLead({ notificationId, reason: 'OnCall' })
          .catch(err => this.#logger.warn('Failed to auto-reject lead on backend', err));

        return;
      }

      this.#dispatch({
        type: TELEPHONY_ACTION_TYPE.SHOW_LEAD_NOTIFICATION,
        payload: {
          notificationId,
          leadData,
          clientState,
          isLiveTransfer: clientState?.callType === 'LiveTransfer',
          receivedAt: Date.now(),
        },
      });

      this.#sendBrowserNotification(
        'Incoming Lead',
        `New lead: ${leadData?.borrowerName ?? 'Unknown'}`,
      );
    } catch (error) {
      this.#logger.error('Failed to process IncomingLead event', error);
    }
  }

  #onCancelNotification(notificationId) {
    this.#dispatch({
      type: TELEPHONY_ACTION_TYPE.DISMISS_LEAD_NOTIFICATION,
      payload: { notificationId },
    });
  }

  #onTextMessageReceived(messageJson) {
    try {
      const message = jsonParse(messageJson);

      this.#dispatch({
        type: TELEPHONY_ACTION_TYPE.TEXT_MESSAGE_RECEIVED,
        payload: message,
      });

      this.#sendBrowserNotification(
        'New Text Message',
        `Message from ${message?.from ?? 'Unknown'}`,
      );
    } catch (error) {
      this.#logger.error('Failed to parse OnTextMessageReceived payload', error);
    }
  }

  #onWebChatMessageReceived(messageJson) {
    try {
      const message = jsonParse(messageJson);

      this.#dispatch({
        type: TELEPHONY_ACTION_TYPE.WEB_CHAT_MESSAGE_RECEIVED,
        payload: message,
      });

      this.#sendBrowserNotification('New Web Chat', 'You have a new web chat message');
    } catch (error) {
      this.#logger.error('Failed to parse OnWebChatMessageReceived payload', error);
    }
  }

  #onCallFailed(reason) {
    this.#dispatch({
      type: TELEPHONY_ACTION_TYPE.CALL_FAILED,
      payload: { reason },
    });
  }

  #onConferenceParticipantJoining(userInfoJson) {
    try {
      const userInfo = jsonParse(userInfoJson);

      this.#dispatch({
        type: TELEPHONY_ACTION_TYPE.CONFERENCE_PARTICIPANT_JOINING,
        payload: userInfo,
      });
    } catch (error) {
      this.#logger.error('Failed to parse ConferenceParticipantJoining payload', error);
    }
  }

  #onConferenceParticipantJoined(participantsJson) {
    try {
      const participants = jsonParse(participantsJson);

      this.#dispatch({
        type: TELEPHONY_ACTION_TYPE.CONFERENCE_PARTICIPANT_JOINED,
        payload: participants,
      });
    } catch (error) {
      this.#logger.error('Failed to parse ConferenceParticipantJoined payload', error);
    }
  }

  #sendBrowserNotification(title, body) {
    if (document.visibilityState === 'visible') {
      return;
    }

    if (Notification.permission === 'granted') {
      new Notification(title, { body, icon: '/favicon.ico' });

      return;
    }

    if (Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification(title, { body, icon: '/favicon.ico' });
        }
      });
    }
  }
}

export default SignalRTelephonyController;
